import React, { useState } from 'react';
import { Container, Table } from 'react-bootstrap';
import { NavLink } from 'react-router-dom';
import { Row } from 'reactstrap';
import useGetExternalIntegrationRequests, { ExternalIntegrationRequestDto } from '../../requests/useGetExternalIntegrationRequests';
import { PaginatedResult } from '../../requests/usePaginatedQuery';
import PaginatedTable from '../Common/Pagination/PaginatedTable';
import QueryView from '../Common/QueryView';
import BoldButton from '../Common/BoldButton';
import KaModal from '../Common/KaModal';
import useLocalStorage from '../Common/useLocalStorage';
import { clipJson, formatJson, formatTimestamp, httpStatusText, statusClassName } from '../Common/ApiEventLogFormatting';

const extractMessage = (responseBody: string | null): string | null => {
  if (!responseBody) return null;
  try {
    const parsed = JSON.parse(responseBody);
    return typeof parsed?.message === 'string' ? parsed.message : null;
  } catch {
    return null;
  }
};

interface RequestRowProps {
  request: ExternalIntegrationRequestDto;
  advanced: boolean;
  onShowRequest: (request: ExternalIntegrationRequestDto) => void;
}

const RequestRow = ({ request, advanced, onShowRequest }: RequestRowProps) => {
  const [responseExpanded, setResponseExpanded] = useState(false);
  const { preview, remaining } = clipJson(request.responseBody);
  const statusCode = request.responseStatusCode;

  return (
    <tr>
      <td>{request.externalIntegrationName || '—'}</td>
      <td>{formatTimestamp(request.timestamp)}</td>
      <td style={{ wordBreak: 'break-all' }}><div>{request.method}</div><div>{request.path}</div></td>
      <td>
        {statusCode !== null &&
          <span className={statusClassName(statusCode)}>
            {advanced && <>{statusCode} </>}
            {httpStatusText(statusCode)}
          </span>}
      </td>
      {!advanced && <td>{extractMessage(request.responseBody) ?? '—'}</td>}
      {advanced && <td>
        <pre className="mb-0 small" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
          {responseExpanded ? formatJson(request.responseBody) : preview}
        </pre>
        {!responseExpanded && remaining > 0 && (
          <button type="button" className="btn btn-link btn-sm p-0 text-muted" onClick={() => setResponseExpanded(true)}>
            [+{remaining} more line{remaining === 1 ? '' : 's'} — click to expand]
          </button>
        )}
      </td>}
      {advanced && <td align="right">
        <button type="button" className="btn btn-primary btn-sm fw-bold" onClick={() => onShowRequest(request)}>
          View Request
        </button>
      </td>}
    </tr>
  );
};

const RequestModal = ({ request, onHide }: { request: ExternalIntegrationRequestDto; onHide: () => void }) => (
  <KaModal
    show={true}
    onHide={onHide}
    size="lg"
    title="Request Details"
    body={<>
      <div className="row gx-4 gy-2 mb-3">
        <div className="col-auto">
          <div className="small fw-semibold text-dark">Timestamp</div>
          <div>{formatTimestamp(request.timestamp)}</div>
        </div>
        <div className="col-auto">
          <div className="small fw-semibold text-dark">Method</div>
          <div>{request.method}</div>
        </div>
        <div className="col-auto">
          <div className="small fw-semibold text-dark">Version</div>
          <div>{request.version ?? '—'}</div>
        </div>
        <div className="col-auto">
          <div className="small fw-semibold text-dark">Status Code</div>
          <div>{request.responseStatusCode ?? '—'}</div>
        </div>
        <div className="col-12">
          <div className="small fw-semibold text-dark">Path</div>
          <div>{request.path}</div>
        </div>
        <div className="col-12">
          <div className="small fw-semibold text-dark">External Integration</div>
          <div>{request.externalIntegrationName || '—'}</div>
        </div>
        <div className="col-12">
          <div className="small fw-semibold text-dark">External Integration ID</div>
          <div>{request.externalIntegrationId}</div>
        </div>
      </div>
      {request.body
        ? <div className="row gx-3">
            <div className="col">
              <div className="small fw-semibold text-dark mb-1">Request Body</div>
              <pre className="border rounded p-2 bg-white small mb-0">{formatJson(request.body)}</pre>
            </div>
            <div className="col">
              <div className="small fw-semibold text-dark mb-1">Response Body</div>
              <pre className="border rounded p-2 bg-white small mb-0">{formatJson(request.responseBody)}</pre>
            </div>
          </div>
        : <>
            <div className="mb-2">
              <div className="small fw-semibold text-dark mb-1">Request Body</div>
              <pre className="border rounded p-2 bg-white small mb-0">—</pre>
            </div>
            <div>
              <div className="small fw-semibold text-dark mb-1">Response Body</div>
              <pre className="border rounded p-2 bg-white small mb-0">{formatJson(request.responseBody)}</pre>
            </div>
          </>
      }
    </>}
    footer={
      <button type="button" className="btn btn-ghost-secondary" onClick={onHide}>Close</button>
    }
  />
);

export const ApiEventLogPage = () => {
  const [currentPage, setCurrentPage] = useState(0);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [advanced, setAdvanced] = useLocalStorage('apiEventLog.advanced', false);
  const [modalRequest, setModalRequest] = useState<ExternalIntegrationRequestDto | null>(null);

  const getRequests = useGetExternalIntegrationRequests(
    { page: currentPage, pageSize: 50 },
    { onSuccess: () => setLastUpdated(new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })) }
  );

  return (
    <Container fluid>
        <Row className="justify-content-between mb-2 align-items-center">
          <h2 className="col-auto ka-blue">API Event Log</h2>
          <div className="col-auto d-flex align-items-center gap-3">
            <div className="form-check form-switch d-flex align-items-center gap-2 mb-0">
              <input
                className="form-check-input mt-0"
                type="checkbox"
                role="switch"
                id="advancedViewToggle"
                checked={advanced}
                onChange={e => setAdvanced(e.target.checked)} />
              <label className="form-check-label" htmlFor="advancedViewToggle">Advanced View</label>
            </div>
            <div className="border-start ps-3 d-flex align-items-center gap-1">
              {lastUpdated && <span className="text-muted small">Updated {lastUpdated}</span>}
              <BoldButton className="btn-ghost-secondary px-2" onClick={() => getRequests.query()}>
                <i className="bi-arrow-clockwise me-1" />Refresh
              </BoldButton>
            </div>
          </div>
        </Row>
        <p className="mb-3">
          <NavLink className="card-link" to=".." relative="path">
            <i className="bi-chevron-left" /> Back
          </NavLink>
        </p>
        <QueryView
          query={getRequests}
          renderData={(data: PaginatedResult<ExternalIntegrationRequestDto>) => (
            data.items.length === 0
              ? <p>No API requests found.</p>
              : <PaginatedTable
                  onPageChange={page => setCurrentPage(page)}
                  currentPage={currentPage}
                  pageSize={50}
                  totalPages={data.totalPages}>
                  <Table bordered>
                    <thead>
                      <tr>
                        <th style={{ verticalAlign: 'top' }}>Integration</th>
                        <th style={{ verticalAlign: 'top' }}>Timestamp</th>
                        <th style={{ verticalAlign: 'top', width: '25%' }}>Path</th>
                        <th style={{ verticalAlign: 'top' }}>Status</th>
                        {!advanced && <th style={{ verticalAlign: 'top' }}>Message</th>}
                        {advanced && <th style={{ verticalAlign: 'top', width: '40%' }}>Response Body</th>}
                        {advanced && <th style={{ verticalAlign: 'top' }} />}
                      </tr>
                    </thead>
                    <tbody>
                      {data.items.map(request =>
                        <RequestRow
                          key={request.id}
                          request={request}
                          advanced={advanced}
                          onShowRequest={setModalRequest} />
                      )}
                    </tbody>
                  </Table>
                </PaginatedTable>
          )} />
        {modalRequest && <RequestModal request={modalRequest} onHide={() => setModalRequest(null)} />}
    </Container>
  );
};
